import React, { type CSSProperties, useMemo } from 'react';
import { Button, TextBadge, Typography } from '@packages/ui/shared';
import type { BadgeVariants, ButtonVariant } from '@packages/ui/shared';
import { getBadgeVariantTechline } from '@web/utils';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { SkeletonLoader } from '@web/components/skeleton-loader';

export interface CaseManagementTask {
    id: string;
    title: string;
    status: string;
    statusLabel?: string;
    statusVariant?: BadgeVariants;
    assignee?: {
        name: string;
        avatarUrl?: string | null;
    };
    roNumber: string;
    caseNumber: string;
    caseUrl?: string;
    worksheetsCount: number;
    worksheets?: string[];
    vin?: string;
    carline?: string;
    lastUpdatedAt: string;
    autoCloseDaysRemaining?: number | null;
    actionLabel?: string;
    actionVariant?: ButtonVariant;
    onAction?: () => void;
}

interface CaseManagementTaskCardProps {
    task: CaseManagementTask;
    hideAction?: boolean;
}

const cardContainerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    width: '100%',
    border: '1px solid #E3E3E3',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
    marginTop: 16,
};

const headerSectionStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#F8F8F8',
    borderBottom: '1px solid #E3E3E3',
    justifyContent: 'space-between',
    flex: '1 1 20%',
    padding: '16px 20px',
    boxSizing: 'border-box',
    gap: 16,
    minWidth: 200,
};

const headerContentStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
};

const assigneeWrapperStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    minWidth: 60,
};

const avatarWrapperStyle: CSSProperties = {
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: '#436CA1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
};

const avatarImageStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
};

const contentSectionStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: '1 1 50%',
    padding: '16px 20px',
    borderBottom: '1px solid #E3E3E3',
    boxSizing: 'border-box',
    minWidth: 260,
    gap: 12,
};

const detailItemStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    whiteSpace: 'nowrap',
    flex: '1 1 auto',
    minWidth: 0,
};

const footerSectionStyle: CSSProperties = {
    display: 'flex',
    flex: '1 1 30%',
    minWidth: 220,
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '16px 24px',
    boxSizing: 'border-box',
};

const detailValueWrapperStyle: CSSProperties = {
    display: 'flex',
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
};

const worksheetsTriggerWrapperStyle: CSSProperties = {
    display: 'flex',
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
};

const MAX_VISIBLE_WORKSHEETS = 1;
const worksheetsStylizedSuffixStyle: CSSProperties = {
    color: '#1F518F',
    marginLeft: 4,
};

export function CaseManagementTaskCard({ task, hideAction }: CaseManagementTaskCardProps) {
    const badgeVariant = useMemo<BadgeVariants>(() => {
        if (task.statusVariant) {
            return task.statusVariant;
        }

        return getBadgeVariantTechline(task.status) ?? 'default';
    }, [task.status, task.statusVariant]);

    const statusText = task.statusLabel ?? task.status;
    const assigneeInitials = useMemo(() => getInitials(task.assignee?.name ?? ''), [task.assignee?.name]);
    const worksheetsList = task.worksheets ?? [];
    const worksheetsTooltipId = useMemo(() => `case-management-worksheets-${task.id}`, [task.id]);
    const {
        visibleText: worksheetsVisibleText,
        extraText: worksheetsExtraText,
        showTooltip: shouldShowWorksheetsTooltip,
    } = useMemo(() => {
        if (!worksheetsList.length) {
            return {
                visibleText: '--',
                extraText: '',
                showTooltip: false,
            };
        }

        const visibleWorksheets = worksheetsList.slice(0, MAX_VISIBLE_WORKSHEETS);
        const hiddenCount = worksheetsList.length - visibleWorksheets.length;

        return {
            visibleText: visibleWorksheets.join(', '),
            extraText: hiddenCount > 0 ? ` ... +${hiddenCount}` : '',
            showTooltip: hiddenCount > 0,
        };
    }, [worksheetsList]);

    const worksheetsValue = shouldShowWorksheetsTooltip ? (
        <OverlayTrigger
            placement="bottom"
            delay={{ show: 200, hide: 150 }}
            trigger={['hover', 'focus']}
            overlay={
                <Tooltip id={worksheetsTooltipId} className="text-start">
                    <div>
                        {worksheetsList.map((worksheet, index) => (
                            <div key={`${worksheetsTooltipId}-${index}`}>{worksheet}</div>
                        ))}
                    </div>
                </Tooltip>
            }
        >
            <div
                style={{
                    ...worksheetsTriggerWrapperStyle,
                    cursor: worksheetsExtraText ? 'pointer' : 'default',
                }}
            >
                <Typography
                    variant="default"
                    numberOfLines={1}
                    style={{
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0,
                    }}
                >
                    <span>{worksheetsVisibleText}</span>
                    {worksheetsExtraText && <span style={worksheetsStylizedSuffixStyle}>{worksheetsExtraText}</span>}
                </Typography>
            </div>
        </OverlayTrigger>
    ) : (
        <Typography variant="default" numberOfLines={1} style={{ minWidth: 0 }}>
            {worksheetsVisibleText}
            {worksheetsExtraText && <span style={worksheetsStylizedSuffixStyle}>{worksheetsExtraText}</span>}
        </Typography>
    );

    const detailItems: Array<{ label: string; value: React.ReactNode }> = [
        {
            label: 'R.O',
            value: (
                <Typography variant="default" numberOfLines={1}>
                    {task.roNumber}
                </Typography>
            ),
        },
        {
            label: 'Case No',
            value: (
                <Typography
                    variant="default"
                    numberOfLines={1}
                    color="blueOne"
                    href={task.caseUrl}
                    style={{ textDecorationLine: 'underline' }}
                >
                    {task.caseNumber}
                </Typography>
            ),
        },
        {
            label: 'Worksheets',
            value: worksheetsValue,
        },
        {
            label: 'Last Update',
            value: (
                <Typography variant="default" numberOfLines={1}>
                    {task.lastUpdatedAt}
                </Typography>
            ),
        },
    ];

    return (
        <div style={cardContainerStyle}>
            <div style={headerSectionStyle}>
                <div style={headerContentStyle}>
                    <Typography variant="h4" numberOfLines={2}>
                        {task.title}
                    </Typography>
                    <TextBadge variant={badgeVariant}>{statusText}</TextBadge>
                </div>
                {task.assignee && (
                    <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 200, hide: 150 }}
                        overlay={<Tooltip id={`assignee-${task.id}`}>{task.assignee?.name}</Tooltip>}
                    >
                        <div style={assigneeWrapperStyle}>
                            <div style={avatarWrapperStyle}>
                                {renderAvatar(task.assignee?.avatarUrl, assigneeInitials)}
                            </div>
                        </div>
                    </OverlayTrigger>
                )}
            </div>
            <div style={contentSectionStyle}>
                {detailItems.map((item) => (
                    <div key={item.label} style={detailItemStyle}>
                        <Typography variant="h6" numberOfLines={1} color="graySix">
                            {`${item.label}:`}
                        </Typography>
                        <div style={detailValueWrapperStyle}>{item.value}</div>
                    </div>
                ))}
            </div>
            <div style={{ ...footerSectionStyle, ...(hideAction ? { visibility: 'hidden' as const } : undefined) }}>
                {task.autoCloseDaysRemaining != null && task.autoCloseDaysRemaining > 0 && (
                    <Typography variant="caption" color="graySix" style={{ marginRight: 'auto' }}>
                        {`After ${formatDaysRemaining(task.autoCloseDaysRemaining)} it will be a auto-closed case`}
                    </Typography>
                )}
                <Button variant={task.actionVariant ?? 'primary'} size="medium" onPress={task.onAction}>
                    {task.actionLabel ?? 'View case'}
                </Button>
            </div>
        </div>
    );
}

function formatDaysRemaining(days: number): string {
    if (days === 14) {
        return '2 weeks';
    }

    if (days === 7) {
        return '1 week';
    }

    return `${days} ${days === 1 ? 'day' : 'days'}`;
}

function renderAvatar(avatarUrl: string | null | undefined, initials: string) {
    if (avatarUrl) {
        return <img src={avatarUrl} alt="Assignee avatar" style={avatarImageStyle} />;
    }

    return <Typography style={{ textTransform: 'uppercase', color: '#FFFFFF' }}>{initials || '--'}</Typography>;
}

function getInitials(name: string): string {
    if (!name) {
        return '';
    }

    const parts = name.trim().split(/\s+/);

    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }

    const first = parts[0][0] ?? '';
    const last = parts[parts.length - 1][0] ?? '';

    return `${first}${last}`.toUpperCase();
}

const skeletonDetailRowStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
};

export function CaseManagementTaskCardSkeleton() {
    return (
        <div style={cardContainerStyle}>
            <div style={headerSectionStyle}>
                <div style={headerContentStyle}>
                    <SkeletonLoader type="text" width="60%" height={20} className="mt-2" />
                    <SkeletonLoader type="badge" width={70} height={24} />
                </div>
                <div style={assigneeWrapperStyle}>
                    <SkeletonLoader type="icon" width={40} height={40} />
                </div>
            </div>
            <div style={contentSectionStyle}>
                {Array.from({ length: 4 }).map((_, index) => (
                    <div key={`skeleton-detail-${index}`} style={skeletonDetailRowStyle}>
                        <SkeletonLoader type="text" width={70} height={16} />
                        <SkeletonLoader type="text" width="45%" height={16} />
                    </div>
                ))}
            </div>
            <div style={footerSectionStyle}>
                <SkeletonLoader type="btn" width={140} height={40} />
            </div>
        </div>
    );
}
